import React, { useEffect, useState } from "react";
import { useTranslationI18 } from "../../../../../services/hooks/useTranslationI18";
import styles from "../../styles/SessionTimeoutPopup.module.scss";

interface SessionTimeoutPopupProps {
  onContinue: () => void;
  onEndSession: () => void;
  countdownStart?: number;
}

const SessionTimeoutPopup: React.FC<SessionTimeoutPopupProps> = ({
  onContinue,
  onEndSession,
  countdownStart = 10,
}) => {
  const { t } = useTranslationI18();
  const [countdown, setCountdown] = useState<number>(countdownStart);

  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  const progress = (countdown / countdownStart) * circumference;

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          onEndSession();

          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [onEndSession]);

  return (
    <div
      className={styles.popup_overlay}
      onClick={onContinue}
      role="button"
      data-testid="session-timeout-popup"
      tabIndex={0}
    >
      <div
        className={styles.popup_container}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.circle_container}>
          <div className={styles.positions_borders} />

          <svg className={styles.circle_svg} width="132" height="132">
            <circle
              className={styles.circle_background}
              cx="66"
              cy="66"
              r={radius}
              fill="transparent"
              strokeWidth="2"
            />
            <circle
              className={styles.circle_progress}
              cx="66"
              cy="66"
              r={radius}
              fill="transparent"
              strokeWidth="3"
              strokeDasharray={circumference}
              strokeDashoffset={circumference - progress}
            />
          </svg>

          <span className={styles.timer}>{countdown}</span>
        </div>
        <p className={styles.message}>{t("common:end-session-title")}</p>
        <p className={styles.sub_message}>{t("common:tap-to-continue")}</p>
        <div className={styles.button_group}>
          <button
            data-testid="continue-btn"
            className={styles.continue_button}
            onClick={onContinue}
          >
            {t("common:continue")}
          </button>
          <button
            data-testid="end-session-btn"
            className={styles.end_session_button}
            onClick={onEndSession}
          >
            {t("common:end-session")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutPopup;
